import { IconButton, RadioLabelText, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import InfoIconTooltip from "app/pages/sales-intelligence/common-components/tooltip/InfoIconTooltip";

import { MAX_FILTERS_QUANTITY } from "../../../constants";
import type { CrmEnrichmentI18nKeys } from "../../../types/translations";
import type { CrmEnrichmentJobFilterType, CrmEnrichmentJobObject } from "../../../types/types";
import CrmEnrichmentJobFilter from "../CrmEnrichmentJobFilter";

import {
    StyledCrmEnrichmentFilterRadioContainer,
    StyledCrmEnrichmentFilterRadioButton,
    StyledCrmEnrichmentFilterAddIconButton,
    StyledCrmEnrichmentFilterRadioLabel,
} from "./styles";

export type CrmEnrichmentJobFiltersProps = {
    translationKeys: CrmEnrichmentI18nKeys;
    selectedJobObject: CrmEnrichmentJobObject | "";
    filters: readonly CrmEnrichmentJobFilterType[];
    isApplyFiltersSelected: boolean | null;
    crmFieldOptions: readonly string[];
    onApplyFiltersChange: (value: boolean) => void;
    onAddFilter: () => void;
    onDeleteFilter: (id: number) => void;
    onCrmFieldFilterChange: ({ id, field }: { id: number; field: string }) => void;
    onFilterChange: ({
        id,
        key,
        value,
    }: {
        id: number;
        key: keyof CrmEnrichmentJobFilterType;
        value: CrmEnrichmentJobFilterType[keyof CrmEnrichmentJobFilterType];
    }) => void;
};

const CrmEnrichmentJobFilters = ({
    translationKeys,
    selectedJobObject,
    filters,
    isApplyFiltersSelected,
    crmFieldOptions,
    onApplyFiltersChange,
    onAddFilter,
    onDeleteFilter,
    onFilterChange,
    onCrmFieldFilterChange,
}: CrmEnrichmentJobFiltersProps) => {
    const isAllOptionSelected = isApplyFiltersSelected !== null && !isApplyFiltersSelected;

    const isAddFilterButtonEnabled = filters.length < MAX_FILTERS_QUANTITY;

    return (
        <>
            <StyledCrmEnrichmentFilterRadioContainer
                data-automation="filter-container-rules-list"
                isSelected={!!isApplyFiltersSelected}
            >
                <Flex alignItems="center">
                    <StyledCrmEnrichmentFilterRadioButton
                        renderLabel={() => (
                            <StyledCrmEnrichmentFilterRadioLabel>
                                <I18n
                                    translatable={{ key: "si.crm-enrichment.job-wizard.filters.create" }}
                                    element={RadioLabelText}
                                />

                                <InfoIconTooltip
                                    tooltipKey={
                                        selectedJobObject == "contact"
                                            ? "si.crm-enrichment.job-wizard.filters.apply_filters.tooltip.contacts"
                                            : "si.crm-enrichment.job-wizard.filters.apply_filters.tooltip"
                                    }
                                />
                            </StyledCrmEnrichmentFilterRadioLabel>
                        )}
                        itemSelected={!!isApplyFiltersSelected}
                        onClick={() => onApplyFiltersChange(true)}
                    />
                </Flex>

                {isApplyFiltersSelected ? (
                    <>
                        {filters.map((filter) => (
                            <CrmEnrichmentJobFilter
                                key={filter.id}
                                filter={filter}
                                translationKeys={translationKeys}
                                crmFieldOptions={crmFieldOptions}
                                isDeleteDisabled={filters.length === 1}
                                onFilterChange={onFilterChange}
                                onCrmFieldFilterChange={onCrmFieldFilterChange}
                                onDeleteFilter={onDeleteFilter}
                            />
                        ))}

                        <Tooltip
                            placement="top"
                            enabled={isAddFilterButtonEnabled}
                            content={<I18n translatable={{ key: "si.crm-enrichment.job-wizard.filters.add" }}></I18n>}
                        >
                            <StyledCrmEnrichmentFilterAddIconButton>
                                <IconButton
                                    iconName="add"
                                    type="flatDark"
                                    isDisabled={!isAddFilterButtonEnabled}
                                    onClick={onAddFilter}
                                />
                            </StyledCrmEnrichmentFilterAddIconButton>
                        </Tooltip>
                    </>
                ) : null}
            </StyledCrmEnrichmentFilterRadioContainer>

            <StyledCrmEnrichmentFilterRadioContainer
                data-automation="filter-container-rules-all"
                data-automation-selected={isAllOptionSelected}
                isSelected={isAllOptionSelected}
            >
                <Flex alignItems="center">
                    <StyledCrmEnrichmentFilterRadioButton
                        renderLabel={() => (
                            <StyledCrmEnrichmentFilterRadioLabel>
                                <I18n
                                    translatable={{
                                        key: translationKeys.allEntriesFilter(selectedJobObject),
                                    }}
                                    element={RadioLabelText}
                                />

                                <InfoIconTooltip
                                    tooltipKey={translationKeys.allEntriesFilterTooltip(selectedJobObject)}
                                />
                            </StyledCrmEnrichmentFilterRadioLabel>
                        )}
                        itemSelected={isAllOptionSelected}
                        onClick={() => onApplyFiltersChange(false)}
                    />
                </Flex>
            </StyledCrmEnrichmentFilterRadioContainer>
        </>
    );
};

export default CrmEnrichmentJobFilters;
